<template>
  <div class="row domain-rules-page">
    <div class="col-lg-12">
      <KTPortlet :title="$t('MENU.RULES')">
        <template v-slot:body>
          <div class="row table-filter align-items-end">
            <div class="col-lg-4">
              <b-form-group
                :label="$t('COMMON.SEL_DOMAIN')"
                label-for="select-domain-rules"
              >
                <model-list-select
                  id="select-domain-rules"
                  :list="selectDomain.allDomains"
                  v-model="selectDomain.selectedDomain"
                  option-value="domain"
                  option-text="domain"
                  :placeholder="$t('COMMON.SEL_DOMAIN')"
                  @input="onChangeDomain"
                />
              </b-form-group>
            </div>
            <div class="col-lg-8 text-lg-right">
              <b-button
                variant="outline-secondary"
                class="mr-2 mb-3 mb-lg-0"
                @click="refreshCurrentDomain"
                :disabled="!selectedDomainName || rulesLoading || editorLoading"
              >
                {{ $t("COMMON.REFRESH") }}
              </b-button>
              <b-button
                variant="primary"
                class="mb-3 mb-lg-0"
                @click="openNewRule"
                :disabled="!selectedDomainName"
              >
                {{ $t("COMMON.NEW") }} Rule
              </b-button>
            </div>
          </div>

          <div v-if="!selectDomain.allDomains.length" class="rules-empty-state">
            No eligible domains are available for rule management.
          </div>

          <div v-else-if="!selectedDomainName" class="rules-empty-state">
            Select a domain to view and manage incoming message rules.
          </div>

          <div v-else>
            <div class="rules-page-header">
              <div>
                <div class="rules-page-title">{{ selectedDomainName }}</div>
                <div class="rules-page-subtitle">
                  Rules run from top to bottom. Draft rules stay inactive until
                  enabled.
                </div>
              </div>
            </div>

            <div v-if="rulesLoading" class="rules-empty-state">
              <b-spinner small class="mr-2" />
              Loading rules...
            </div>

            <div v-else-if="!rules.length" class="rules-empty-state">
              No rules exist for this domain yet.
            </div>

            <div v-else class="rules-table-wrap">
              <table class="rules-table">
                <thead>
                  <tr>
                    <th class="rules-table__pos">#</th>
                    <th>Rule</th>
                    <th class="rules-table__status">Status</th>
                    <th class="rules-table__actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="rule in rules" :key="rule.id">
                    <td class="rules-table__pos-cell" data-label="#">
                      <div class="rules-position">
                        <span class="rules-position__value">{{ rule.priority }}</span>
                        <div class="rules-position__buttons">
                          <button
                            type="button"
                            class="rules-arrow-btn"
                            @click="moveRule(rule, 'up')"
                            aria-label="Move rule up"
                          >
                            ↑
                          </button>
                          <button
                            type="button"
                            class="rules-arrow-btn"
                            @click="moveRule(rule, 'down')"
                            aria-label="Move rule down"
                          >
                            ↓
                          </button>
                        </div>
                      </div>
                    </td>
                    <td data-label="Rule">
                      <div class="rules-table__name">
                        {{ rule.rulename }}
                        <span
                          v-if="rule.rule_kind === 'vacation'"
                          class="badge badge-light-info ml-2"
                        >
                          Vacation
                        </span>
                      </div>
                    </td>
                    <td data-label="Status">
                      <span
                        class="badge"
                        :class="rule.enabled == 1 ? 'badge-success' : 'badge-secondary'"
                      >
                        {{ rule.enabled == 1 ? $t("COMMON.ENABLED") : "Draft" }}
                      </span>
                    </td>
                    <td data-label="Actions">
                      <div class="rules-table__action-group">
                        <b-button
                          size="sm"
                          variant="primary"
                          class="mr-1 mb-1"
                          @click="openRule(rule.id)"
                        >
                          Edit
                        </b-button>
                        <b-button
                          size="sm"
                          variant="outline-info"
                          class="mr-1 mb-1"
                          @click="duplicateRule(rule)"
                        >
                          {{ $t("COMMON.COPY") }}
                        </b-button>
                        <b-button
                          size="sm"
                          :variant="rule.enabled == 1 ? 'outline-warning' : 'outline-success'"
                          class="mr-1 mb-1"
                          @click="toggleRuleEnabled(rule)"
                        >
                          {{ rule.enabled == 1 ? $t("COMMON.DISABLE") : $t("COMMON.ENABLE") }}
                        </b-button>
                        <b-button
                          size="sm"
                          variant="outline-danger"
                          class="mb-1"
                          @click="deleteRule(rule)"
                        >
                          {{ $t("COMMON.DELETE") }}
                        </b-button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </template>
      </KTPortlet>

      <b-modal
        id="domain-rule-modal"
        ref="ruleModal"
        :title="ruleModalTitle"
        size="xl"
        scrollable
        modal-class="rules-modal-shell"
        content-class="rules-modal-content"
        @hidden="onRuleModalHidden"
      >
        <div v-if="editorLoading" class="rules-empty-state">
          <b-spinner small class="mr-2" />
          Loading rule details...
        </div>

        <div v-else>
          <div class="rule-modal-header">
            <div v-if="isVacationRule" class="rule-modal-subtitle">
              Build a vacation responder rule with conditions, optional
              exceptions, one reply action, and schedule settings.
            </div>
            <div class="rule-modal-badges">
              <span
                class="badge mr-2"
                :class="
                  isVacationRule ? 'badge-light-info' : 'badge-light-primary'
                "
              >
                {{ isVacationRule ? "Vacation Rule" : "Standard Rule" }}
              </span>
              <span
                class="badge mr-2"
                :class="editor.meta.enabled == 1 ? 'badge-success' : 'badge-secondary'"
              >
                {{ editor.meta.enabled == 1 ? $t("COMMON.ENABLED") : "Draft" }}
              </span>
              <span
                v-if="editor.meta.special_id"
                class="badge badge-light-warning"
              >
                System Rule
              </span>
            </div>
          </div>

          <div class="row">
            <div class="col-md-6">
              <b-form-group label="Rule Name">
                <b-form-input
                  v-model.trim="editor.meta.rulename"
                  maxlength="150"
                  placeholder="Example: Block forged sender"
                />
              </b-form-group>
            </div>
            <div class="col-md-3">
              <b-form-group label="Rule Type">
                <b-form-select
                  v-model="editor.meta.rule_kind"
                  :options="ruleKindOptions"
                  value-field="value"
                  text-field="text"
                  :disabled="editor.mode === 'edit'"
                  @change="onRuleKindChange"
                />
              </b-form-group>
            </div>
            <div class="col-md-3">
              <b-form-group label="Match Mode">
                <b-form-select
                  v-model="editor.meta.match_mode"
                  :options="matchModeOptions"
                  value-field="value"
                  text-field="text"
                />
              </b-form-group>
            </div>
          </div>

          <div class="row">
            <div class="col-md-3">
              <b-form-group label="Stop Processing">
                <b-form-select
                  v-model="editor.meta.stop_processing"
                  :options="booleanOptions"
                  value-field="value"
                  text-field="text"
                />
              </b-form-group>
            </div>
          </div>

          <div v-if="isVacationRule" class="rule-modal-section">
            <div class="rule-modal-section__header">
              <div>
                <div class="rule-modal-section__title">Vacation Settings</div>
                <div class="rule-modal-section__subtitle">
                  Configure the reply subject and optional active window.
                </div>
              </div>
            </div>

            <div class="row">
              <div class="col-md-6">
                <b-form-group label="Vacation Reply Subject">
                  <b-form-input
                    v-model.trim="editor.vacation.reply_subject"
                    maxlength="255"
                    placeholder="Optional subject line"
                  />
                </b-form-group>
              </div>
            </div>

            <div class="row">
              <div class="col-md-6">
                <b-form-group label="Start">
                  <b-form-input
                    v-model="editor.vacation.start_local"
                    type="datetime-local"
                  />
                </b-form-group>
              </div>
              <div class="col-md-6">
                <b-form-group label="End">
                  <b-form-input
                    v-model="editor.vacation.end_local"
                    type="datetime-local"
                  />
                </b-form-group>
              </div>
            </div>

            <div class="text-muted">
              Leave both dates blank to keep the vacation rule always
              available. If you set one date, you must set both.
            </div>
          </div>

          <div class="rule-modal-section">
            <div class="rule-modal-section__header">
              <div>
                <div class="rule-modal-section__title">Conditions</div>
                <div class="rule-modal-section__subtitle">
                  Apply this rule when
                  {{ editor.meta.match_mode === "any" ? "any" : "all" }} of
                  these conditions match.
                </div>
              </div>
              <b-button
                size="sm"
                variant="outline-primary"
                @click="openConditionEditor('conditions')"
              >
                {{ $t("COMMON.ADD") }} Condition
              </b-button>
            </div>

            <div v-if="!editor.conditions.length" class="rules-empty-state">
              No conditions added yet.
            </div>
            <div v-else class="rule-item-list">
              <div
                v-for="condition in editor.conditions"
                :key="'condition-' + condition.id"
                class="rule-item-row"
              >
                <div class="rule-item-row__body">
                  <div class="rule-item-row__title">
                    {{ summarizeCondition(condition) }}
                  </div>
                </div>
                <div class="rule-item-row__actions">
                  <b-button size="sm" variant="outline-info" class="mr-1 mb-1" @click="openConditionEditor('conditions', condition)">{{ $t("COMMON.EDIT") }}</b-button>
                  <b-button size="sm" variant="outline-danger" class="mb-1" @click="deleteCondition('conditions', condition)">{{ $t("COMMON.DELETE") }}</b-button>
                </div>
              </div>
            </div>
          </div>

          <div class="rule-modal-section">
            <div class="rule-modal-section__header">
              <div>
                <div class="rule-modal-section__title">Exceptions</div>
                <div class="rule-modal-section__subtitle">
                  Skip this rule if any of these exceptions match.
                </div>
              </div>
              <b-button
                size="sm"
                variant="outline-primary"
                @click="openConditionEditor('exceptions')"
              >
                {{ $t("COMMON.ADD") }} Exception
              </b-button>
            </div>

            <div v-if="!editor.exceptions.length" class="rules-empty-state">
              No exceptions added yet.
            </div>
            <div v-else class="rule-item-list">
              <div
                v-for="exception in editor.exceptions"
                :key="'exception-' + exception.id"
                class="rule-item-row"
              >
                <div class="rule-item-row__body">
                  <div class="rule-item-row__title">
                    {{ summarizeCondition(exception) }}
                  </div>
                </div>
                <div class="rule-item-row__actions">
                  <b-button size="sm" variant="outline-info" class="mr-1 mb-1" @click="openConditionEditor('exceptions', exception)">{{ $t("COMMON.EDIT") }}</b-button>
                  <b-button size="sm" variant="outline-danger" class="mb-1" @click="deleteCondition('exceptions', exception)">{{ $t("COMMON.DELETE") }}</b-button>
                </div>
              </div>
            </div>
          </div>

          <div class="rule-modal-section">
            <div class="rule-modal-section__header">
              <div>
                <div class="rule-modal-section__title">Actions</div>
                <div class="rule-modal-section__subtitle">
                  {{
                    isVacationRule
                      ? "Vacation rules support exactly one reply action."
                      : "Run these actions in order when the rule matches."
                  }}
                </div>
              </div>
              <b-button
                size="sm"
                variant="outline-primary"
                @click="openActionEditor()"
                :disabled="vacationActionLimitReached"
              >
                {{ isVacationRule ? "Add Vacation Reply" : $t("COMMON.ADD") + " Action" }}
              </b-button>
            </div>

            <div v-if="!editor.actions.length" class="rules-empty-state">
              {{
                isVacationRule
                  ? "No vacation reply action added yet."
                  : "No actions added yet."
              }}
            </div>
            <div v-else class="rule-item-list">
              <div
                v-for="action in editor.actions"
                :key="'action-' + action.id"
                class="rule-item-row"
              >
                <div class="rule-item-row__body">
                  <div class="rule-item-row__title">
                    {{ summarizeAction(action) }}
                  </div>
                </div>
                <div class="rule-item-row__actions">
                  <b-button v-if="action.action_type !== 'delete'" size="sm" variant="outline-info" class="mr-1 mb-1" @click="openActionEditor(action)">{{ $t("COMMON.EDIT") }}</b-button>
                  <b-button size="sm" variant="outline-danger" class="mb-1" @click="deleteAction(action)">{{ $t("COMMON.DELETE") }}</b-button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <template v-slot:modal-footer>
          <div class="rules-modal-footer">
            <b-button variant="outline-secondary" @click="closeRuleModal">
              {{ $t("COMMON.CLOSE") }}
            </b-button>
            <div class="rules-modal-footer__actions">
              <b-button variant="primary" class="mr-2" @click="saveRule">
                {{ editor.mode === "create" ? "Create Rule" : $t("COMMON.SAVE") }}
              </b-button>
              <b-button
                v-if="editor.mode === 'edit'"
                :variant="editor.meta.enabled == 1 ? 'warning' : 'success'"
                @click="toggleRuleEnabled(editor.meta)"
              >
                {{ editor.meta.enabled == 1 ? $t("COMMON.DISABLE") : $t("COMMON.ENABLE") }}
              </b-button>
            </div>
          </div>
        </template>
      </b-modal>

      <b-modal
        id="domain-rule-segment-modal"
        ref="segmentModal"
        :title="segmentModalTitle"
        scrollable
        hide-header-close
        no-close-on-backdrop
        no-close-on-esc
        modal-class="rules-child-modal-shell"
        content-class="rules-child-modal-content"
        @hidden="onSegmentModalHidden"
      >
        <div class="child-modal-header">
          <b-button variant="link" class="child-modal-back" @click="closeConditionEditor">
            Back to Rule
          </b-button>
          <div class="child-modal-subtitle">
            {{
              conditionEditor.section === "conditions"
                ? "Define when the rule should match."
                : "Define when the rule should be skipped."
            }}
          </div>
        </div>

        <div class="row">
          <div class="col-md-4">
            <b-form-group label="Field">
              <b-form-select
                v-model="conditionEditor.form.condition_type"
                :options="conditionTypeOptions"
                value-field="value"
                text-field="text"
                @change="onConditionTypeChange"
              />
            </b-form-group>
          </div>
          <div
            v-if="!isStructuredCondition(conditionEditor.form.condition_type)"
            class="col-md-4"
          >
            <b-form-group label="Comparison">
              <b-form-select
                v-model="conditionEditor.form.compare_type"
                :options="compareOptionsFor(conditionEditor.form.condition_type)"
                value-field="value"
                text-field="text"
              />
            </b-form-group>
          </div>
          <div class="col-md-4 d-flex align-items-center">
            <b-form-checkbox
              v-model="conditionEditor.form.negate"
              value="1"
              unchecked-value="0"
              switch
            >
              Negate match
            </b-form-checkbox>
          </div>
        </div>

        <div v-if="conditionValueMode(conditionEditor.form.condition_type) !== 'none'" class="row">
          <div class="col-md-12">
            <b-form-group label="Value">
              <b-form-select
                v-if="conditionValueMode(conditionEditor.form.condition_type) === 'select'"
                v-model="conditionEditor.form.compare_value"
                :options="conditionValueOptions(conditionEditor.form.condition_type)"
                value-field="value"
                text-field="text"
              />
              <b-form-input
                v-else
                v-model.trim="conditionEditor.form.compare_value"
                :type="conditionValueMode(conditionEditor.form.condition_type) === 'number' ? 'number' : 'text'"
                :min="conditionValueMode(conditionEditor.form.condition_type) === 'number' ? 1 : null"
                :placeholder="conditionValuePlaceholder(conditionEditor.form.condition_type)"
              />
            </b-form-group>
          </div>
        </div>

        <div v-else class="text-muted mb-3">
          This condition is evaluated by the filtering engine and does not need
          a comparison value.
        </div>

        <template v-slot:modal-footer>
          <div class="rules-modal-footer">
            <b-button variant="outline-secondary" @click="closeConditionEditor">
              Back
            </b-button>
            <b-button variant="primary" @click="submitConditionEditor">
              {{ conditionEditor.mode === "edit" ? $t("COMMON.SAVE") : $t("COMMON.ADD") }}
            </b-button>
          </div>
        </template>
      </b-modal>

      <b-modal
        id="domain-rule-action-modal"
        ref="actionModal"
        :title="actionModalTitle"
        scrollable
        hide-header-close
        no-close-on-backdrop
        no-close-on-esc
        modal-class="rules-child-modal-shell"
        content-class="rules-child-modal-content"
        @hidden="onActionModalHidden"
      >
        <div class="child-modal-header">
          <b-button variant="link" class="child-modal-back" @click="closeActionEditor">
            Back to Rule
          </b-button>
          <div class="child-modal-subtitle">
            {{
              isVacationRule
                ? "Configure the vacation reply body for this rule."
                : "Configure what happens when the rule matches."
            }}
          </div>
        </div>

        <div class="row">
          <div v-if="!isVacationRule" class="col-md-4">
            <b-form-group label="Action Type">
              <b-form-select
                v-model="actionEditor.form.action_type"
                :options="availableActionTypeOptions"
                value-field="value"
                text-field="text"
                @change="onActionTypeChange"
              />
            </b-form-group>
          </div>
          <div v-else class="col-md-12">
            <b-alert show variant="light" class="mb-3">
              Vacation rules only support one reply action.
            </b-alert>
          </div>
          <div v-if="actionNeedsReplyType(actionEditor.form.action_type)" class="col-md-4">
            <b-form-group label="Reply Format">
              <b-form-select
                v-model="actionEditor.form.reply_type"
                :options="replyTypeOptions"
                value-field="value"
                text-field="text"
              />
            </b-form-group>
          </div>
          <div v-if="actionNeedsEmail(actionEditor.form.action_type)" class="col-md-8">
            <b-form-group label="Destination Email">
              <b-form-input
                v-model.trim="actionEditor.form.email"
                type="email"
                placeholder="recipient@example.com"
              />
            </b-form-group>
          </div>
        </div>

        <div v-if="actionNeedsText(actionEditor.form.action_type)" class="row">
          <div class="col-md-12">
            <b-form-group :label="actionTextLabel(actionEditor.form.action_type)">
              <vue-editor
                v-if="
                  actionEditor.form.action_type === 'reply' &&
                    actionEditor.form.reply_type === '1'
                "
                v-model="actionEditor.form.reply_text"
              />
              <b-form-textarea
                v-else
                v-model="actionEditor.form.reply_text"
                rows="6"
                max-rows="12"
                :placeholder="actionTextPlaceholder(actionEditor.form.action_type)"
              />
            </b-form-group>
          </div>
        </div>

        <div v-if="actionEditor.form.action_type === 'delete'" class="text-muted mb-3">
          This action deletes the message immediately and does not need
          additional settings.
        </div>

        <template v-slot:modal-footer>
          <div class="rules-modal-footer">
            <b-button variant="outline-secondary" @click="closeActionEditor">
              Back
            </b-button>
            <b-button variant="primary" @click="submitActionEditor">
              {{ actionEditor.mode === "edit" ? $t("COMMON.SAVE") : $t("COMMON.ADD") }}
            </b-button>
          </div>
        </template>
      </b-modal>
    </div>
  </div>
</template>

<script>
import { SET_BREADCRUMB } from "@/store/breadcrumbs.module";
import KTPortlet from "@/views/partials/content/Portlet.vue";
import { postRequest } from "@/api/request.js";
import { filterDisplayableDomains } from "@/common/domainFilters";
import { VueEditor } from "vue2-editor";

function emptyRuleMeta() {
  return {
    id: null,
    rulename: "",
    rule_kind: "normal",
    match_mode: "all",
    stop_processing: "1",
    enabled: 0,
    special_id: null,
  };
}

function emptyVacationSettings() {
  const now = new Date();
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  const end = new Date(now);
  end.setHours(23, 59, 0, 0);

  return {
    reply_subject: "",
    start_local: toLocalDateTimeInput(start),
    end_local: toLocalDateTimeInput(end),
  };
}

function toLocalDateTimeInput(date) {
  const pad = function(part) {
    return String(part).padStart(2, "0");
  };

  return (
    date.getFullYear() +
    "-" +
    pad(date.getMonth() + 1) +
    "-" +
    pad(date.getDate()) +
    "T" +
    pad(date.getHours()) +
    ":" +
    pad(date.getMinutes())
  );
}

function emptyConditionForm() {
  return {
    condition_type: "from",
    negate: "0",
    compare_type: "ct",
    compare_value: "",
  };
}

function emptyActionForm(actionType = "delete") {
  return {
    action_type: actionType,
    email: "",
    reply_type: "1",
    reply_text: "",
  };
}

export default {
  name: "domainrules",
  components: {
    KTPortlet,
    VueEditor,
  },
  data() {
    return {
      selectDomain: {
        allDomains: [],
        selectedDomain: {},
      },
      rules: [],
      rulesLoading: false,
      editorLoading: false,
      editor: {
        mode: null,
        rid: null,
        meta: emptyRuleMeta(),
        vacation: emptyVacationSettings(),
        conditions: [],
        exceptions: [],
        actions: [],
      },
      conditionEditor: {
        visible: false,
        mode: "add",
        section: "conditions",
        itemId: null,
        form: emptyConditionForm(),
      },
      actionEditor: {
        visible: false,
        mode: "add",
        itemId: null,
        form: emptyActionForm(),
      },
      modalFlow: {
        nextModal: null,
        reopenRuleModal: false,
      },
      conditionTypeOptions: [
        { value: "from", text: "From" },
        { value: "envelopefrom", text: "Envelope From" },
        { value: "envelopeto", text: "Envelope To" },
        { value: "to", text: "To" },
        { value: "cc", text: "CC" },
        { value: "tocc", text: "To or CC" },
        { value: "subject", text: "Subject" },
        { value: "body", text: "Body" },
        { value: "msize", text: "Message Size" },
        { value: "listid", text: "List-ID" },
        { value: "received", text: "Received" },
        { value: "returnpath", text: "Return-Path" },
        { value: "replyto", text: "Reply-To" },
        { value: "xmailer", text: "X-Mailer" },
        { value: "xvirus", text: "X-Virus" },
        { value: "xpolarisspam", text: "Spam status" },
        { value: "isauthenticated", text: "External Message" },
        { value: "attachmenttype", text: "Attachment Type" },
        { value: "detectforgedfrom", text: "Detect Forged From" },
      ],
      matchModeOptions: [
        { value: "all", text: "All conditions must match" },
        { value: "any", text: "Any condition may match" },
      ],
      ruleKindOptions: [
        { value: "normal", text: "Standard Rule" },
        { value: "vacation", text: "Vacation Message" },
      ],
      booleanOptions: [
        { value: "1", text: "Yes" },
        { value: "0", text: "No" },
      ],
      textCompareOptions: [
        { value: "ct", text: "Contains" },
        { value: "eq", text: "Equals" },
        { value: "sw", text: "Starts With" },
        { value: "ew", text: "Ends With" },
      ],
      numericCompareOptions: [
        { value: "gt", text: "Greater Than" },
        { value: "lt", text: "Less Than" },
      ],
      actionTypeOptions: [
        { value: "delete", text: "Delete Message" },
        { value: "reject", text: "Reject Message" },
        { value: "redirect", text: "Redirect Message" },
        { value: "forward_copy", text: "Forward Copy" },
        { value: "reply", text: "Reply" },
        { value: "warning_message", text: "Add Warning Message" },
      ],
      replyTypeOptions: [
        { value: "1", text: "HTML" },
        { value: "2", text: "Plain Text" },
      ],
      spamValueOptions: [
        { value: "Spam", text: "Spam" },
        { value: "Likely", text: "Possible Spam" },
        { value: "Whitelisted", text: "Whitelisted" },
        { value: "Blacklisted", text: "Blacklisted" },
      ],
    };
  },
  computed: {
    selectedDomainName() {
      if (
        !this.selectDomain.selectedDomain ||
        !this.selectDomain.selectedDomain.domain
      ) {
        return "";
      }

      return this.selectDomain.selectedDomain.domain;
    },
    editorHasPersistedRule() {
      return this.editor.mode === "edit" && !!this.editor.rid;
    },
    isVacationRule() {
      return this.editor.meta.rule_kind === "vacation";
    },
    vacationActionLimitReached() {
      return this.isVacationRule && this.editor.actions.length >= 1;
    },
    availableActionTypeOptions() {
      if (this.isVacationRule) {
        return this.actionTypeOptions.filter(function(option) {
          return option.value === "reply";
        });
      }

      return this.actionTypeOptions;
    },
    ruleModalTitle() {
      return this.editor.mode === "create"
        ? this.editor.meta.rule_kind === "vacation"
          ? "New Vacation Rule"
          : "New Rule"
        : this.editor.meta.rulename || "Edit Rule";
    },
    segmentModalTitle() {
      const noun =
        this.conditionEditor.section === "conditions"
          ? "Condition"
          : "Exception";

      return this.conditionEditor.mode === "edit"
        ? "Edit " + noun
        : "New " + noun;
    },
    actionModalTitle() {
      return this.actionEditor.mode === "edit" ? "Edit Action" : "New Action";
    },
  },
  created() {
    this.$store.dispatch(SET_BREADCRUMB, [{ title: this.$t("MENU.RULES") }]);
    this.loadDomains();
  },
  methods: {
    async loadDomains() {
      const res = await postRequest({
        action: "getAllDomains",
        token: localStorage.id_token,
      });

      if (!res) {
        this.makeToastVariant("danger", "Could not load domains.");
        return;
      }

      this.selectDomain.allDomains = filterDisplayableDomains(res);

      if (!this.selectDomain.allDomains.length) {
        this.selectDomain.selectedDomain = {};
        return;
      }

      this.selectDomain.selectedDomain = this.selectDomain.allDomains[0];
      await this.loadRules(this.selectedDomainName);
    },

    async onChangeDomain(newValue) {
      if (!newValue || !newValue.domain) {
        this.resetEditor();
        return;
      }

      this.hideAllModals();
      this.resetEditor();
      await this.loadRules(newValue.domain);
    },

    async refreshCurrentDomain() {
      if (!this.selectedDomainName) {
        return;
      }

      await this.loadRules(this.selectedDomainName, this.editor.rid);
    },

    async loadRules(domain, preferredRuleId = null) {
      this.rulesLoading = true;

      const res = await postRequest({
        action: "getDomainIncomingRules",
        token: localStorage.id_token,
        domain: domain,
      });

      this.rulesLoading = false;

      if (!res || !res.returncode) {
        this.rules = [];
        this.resetEditor();
        this.makeToastVariant(
          "danger",
          this.responseMessage(res, "Could not load rules.")
        );
        return;
      }

      this.rules = Array.isArray(res.returndata) ? res.returndata : [];

      if (!this.rules.length) {
        if (!preferredRuleId) {
          this.resetEditor();
        }
        return;
      }

      if (preferredRuleId) {
        const hasTargetRule = this.rules.some(function(rule) {
          return String(rule.id) === String(preferredRuleId);
        });
        if (hasTargetRule) {
          await this.loadRule(preferredRuleId);
        }
      }
    },

    async openRule(ruleId) {
      this.editorLoading = true;
      this.$bvModal.show("domain-rule-modal");
      await this.loadRule(ruleId);
    },

    async loadRule(ruleId) {
      if (!this.selectedDomainName || !ruleId) {
        this.editorLoading = false;
        return;
      }

      this.editorLoading = true;

      const ruleRes = await postRequest({
        action: "getDomainIncomingRule",
        token: localStorage.id_token,
        domain: this.selectedDomainName,
        rid: ruleId,
      });

      if (!ruleRes || !ruleRes.returncode) {
        this.editorLoading = false;
        this.makeToastVariant("danger", "Could not load the selected rule.");
        return;
      }

      const ruleData = ruleRes.returndata || {};
      const requests = await Promise.all([
        postRequest({
          action: "getDomainRuleConditions",
          token: localStorage.id_token,
          domain: this.selectedDomainName,
          rid: ruleId,
        }),
        postRequest({
          action: "getDomainRuleExceptions",
          token: localStorage.id_token,
          domain: this.selectedDomainName,
          rid: ruleId,
        }),
        postRequest({
          action: "getDomainRuleActions",
          token: localStorage.id_token,
          domain: this.selectedDomainName,
          rid: ruleId,
        }),
        ruleData.rule_kind === "vacation"
          ? postRequest({
              action: "getDomainRuleVacationSettings",
              token: localStorage.id_token,
              domain: this.selectedDomainName,
              rid: ruleId,
            })
          : Promise.resolve({ returncode: 1, returndata: null }),
      ]);

      this.editorLoading = false;

      if (requests.some((entry) => !entry || !entry.returncode)) {
        this.makeToastVariant("danger", "Could not load the selected rule.");
        return;
      }

      this.editor.mode = "edit";
      this.editor.rid = ruleData.id;
      this.editor.meta = {
        id: ruleData.id,
        rulename: ruleData.rulename || "",
        rule_kind: ruleData.rule_kind || "normal",
        match_mode: ruleData.match_mode || "all",
        stop_processing: String(ruleData.stop_processing == 0 ? 0 : 1),
        enabled: Number(ruleData.enabled || 0),
        special_id: ruleData.special_id || null,
      };
      this.editor.vacation =
        ruleData.rule_kind === "vacation"
          ? this.mapVacationSettingsToForm(requests[3].returndata || {})
          : emptyVacationSettings();
      this.editor.conditions = Array.isArray(requests[0].returndata)
        ? requests[0].returndata
        : [];
      this.editor.exceptions = Array.isArray(requests[1].returndata)
        ? requests[1].returndata
        : [];
      this.editor.actions = Array.isArray(requests[2].returndata)
        ? requests[2].returndata
        : [];
    },

    openNewRule() {
      this.resetEditor();
      this.editor.mode = "create";
      this.$bvModal.show("domain-rule-modal");
    },

    onRuleKindChange() {
      if (this.editor.mode !== "create") {
        return;
      }

      if (this.editor.meta.rule_kind === "vacation") {
        this.editor.meta.stop_processing = "0";
        this.editor.vacation = emptyVacationSettings();
        return;
      }

      this.editor.meta.stop_processing = "1";
    },

    closeRuleModal() {
      this.$bvModal.hide("domain-rule-modal");
    },

    onRuleModalHidden() {
      if (this.modalFlow.nextModal === "segment") {
        this.modalFlow.nextModal = null;
        this.$nextTick(() => {
          this.$bvModal.show("domain-rule-segment-modal");
        });
        return;
      }

      if (this.modalFlow.nextModal === "action") {
        this.modalFlow.nextModal = null;
        this.$nextTick(() => {
          this.$bvModal.show("domain-rule-action-modal");
        });
        return;
      }

      this.resetEditor();
    },

    onSegmentModalHidden() {
      this.conditionEditor.visible = false;
      this.conditionEditor.mode = "add";
      this.conditionEditor.section = "conditions";
      this.conditionEditor.itemId = null;
      this.conditionEditor.form = emptyConditionForm();

      if (this.modalFlow.reopenRuleModal) {
        this.modalFlow.reopenRuleModal = false;
        this.$nextTick(() => {
          this.$bvModal.show("domain-rule-modal");
        });
      }
    },

    onActionModalHidden() {
      this.actionEditor.visible = false;
      this.actionEditor.mode = "add";
      this.actionEditor.itemId = null;
      this.actionEditor.form = emptyActionForm();

      if (this.modalFlow.reopenRuleModal) {
        this.modalFlow.reopenRuleModal = false;
        this.$nextTick(() => {
          this.$bvModal.show("domain-rule-modal");
        });
      }
    },

    hideAllModals() {
      this.$bvModal.hide("domain-rule-modal");
      this.$bvModal.hide("domain-rule-segment-modal");
      this.$bvModal.hide("domain-rule-action-modal");
    },

    resetEditor() {
      this.modalFlow.nextModal = null;
      this.modalFlow.reopenRuleModal = false;
      this.editorLoading = false;
      this.editor.mode = null;
      this.editor.rid = null;
      this.editor.meta = emptyRuleMeta();
      this.editor.vacation = emptyVacationSettings();
      this.editor.conditions = [];
      this.editor.exceptions = [];
      this.editor.actions = [];
      this.conditionEditor.visible = false;
      this.conditionEditor.mode = "add";
      this.conditionEditor.section = "conditions";
      this.conditionEditor.itemId = null;
      this.conditionEditor.form = emptyConditionForm();
      this.actionEditor.visible = false;
      this.actionEditor.mode = "add";
      this.actionEditor.itemId = null;
      this.actionEditor.form = emptyActionForm();
    },

    async persistRule(showSuccessToast = true) {
      if (!this.selectedDomainName) {
        return false;
      }

      if (!this.editor.meta.rulename) {
        this.makeToastVariant("danger", "Rule name is required.");
        return false;
      }

      const payload = {
        token: localStorage.id_token,
        domain: this.selectedDomainName,
        rulename: this.editor.meta.rulename,
        match_mode: this.editor.meta.match_mode,
        stop_processing: this.editor.meta.stop_processing,
      };

      let action = "addDomainIncomingRule";

      if (this.editor.mode === "edit" && this.editor.rid) {
        action = "editDomainIncomingRule";
        payload.rid = this.editor.rid;
      } else {
        payload.rule_kind = this.editor.meta.rule_kind;
      }

      payload.action = action;

      const res = await postRequest(payload);

      if (!res || !res.returncode) {
        this.makeToastVariant(
          "danger",
          this.responseMessage(res, "Could not save rule.")
        );
        return false;
      }

      const targetRuleId = res.returnid || this.editor.rid;

      if (
        this.editor.meta.rule_kind === "vacation" &&
        !(await this.saveVacationSettings(targetRuleId))
      ) {
        await this.loadRules(this.selectedDomainName, targetRuleId);
        return false;
      }

      if (showSuccessToast) {
        this.makeToastVariant(
          "success",
          this.responseMessage(res, "Rule saved.")
        );
      }
      await this.loadRules(this.selectedDomainName, targetRuleId);
      return true;
    },

    async saveRule() {
      await this.persistRule(true);
    },

    async moveRule(rule, direction) {
      const action =
        direction === "up" ? "moveDomainRuleUp" : "moveDomainRuleDown";
      const res = await postRequest({
        action: action,
        token: localStorage.id_token,
        domain: this.selectedDomainName,
        rid: rule.id,
      });

      if (!res || !res.returncode) {
        this.makeToastVariant(
          "danger",
          this.responseMessage(res, "Could not move rule.")
        );
        return;
      }

      await this.loadRules(this.selectedDomainName, rule.id);
    },

    async duplicateRule(rule) {
      const res = await postRequest({
        action: "duplicateDomainIncomingRule",
        token: localStorage.id_token,
        domain: this.selectedDomainName,
        rid: rule.id,
      });

      if (!res || !res.returncode) {
        this.makeToastVariant(
          "danger",
          this.responseMessage(res, "Could not duplicate rule.")
        );
        return;
      }

      this.makeToastVariant(
        "success",
        this.responseMessage(res, "Rule duplicated.")
      );
      await this.loadRules(this.selectedDomainName, res.returnid || rule.id);
    },

    async deleteRule(rule) {
      if (
        !window.confirm(
          'Delete rule "' +
            rule.rulename +
            '" and all of its conditions, exceptions, and actions?'
        )
      ) {
        return;
      }

      const res = await postRequest({
        action: "removeDomainIncomingRule",
        token: localStorage.id_token,
        domain: this.selectedDomainName,
        rid: rule.id,
      });

      if (!res || !res.returncode) {
        this.makeToastVariant(
          "danger",
          this.responseMessage(res, "Could not delete rule.")
        );
        return;
      }

      this.makeToastVariant(
        "success",
        this.responseMessage(res, "Rule deleted.")
      );

      if (this.editor.rid === rule.id) {
        this.hideAllModals();
        this.resetEditor();
      }

      await this.loadRules(this.selectedDomainName);
    },

    async toggleRuleEnabled(rule) {
      const action =
        Number(rule.enabled) === 1 ? "deactivateDomainRule" : "activateDomainRule";
      const res = await postRequest({
        action: action,
        token: localStorage.id_token,
        domain: this.selectedDomainName,
        rid: rule.id,
      });

      if (!res || !res.returncode) {
        this.makeToastVariant(
          "danger",
          this.responseMessage(res, "Could not update rule status.")
        );
        return;
      }

      this.makeToastVariant(
        "success",
        this.responseMessage(res, "Rule updated.")
      );
      await this.loadRules(this.selectedDomainName, rule.id);
    },

    async openConditionEditor(section, item = null) {
      if (!item && !this.editorHasPersistedRule) {
        const persisted = await this.persistRule(false);
        if (!persisted) {
          return;
        }
      }

      this.conditionEditor.visible = true;
      this.conditionEditor.section = section;
      this.conditionEditor.mode = item ? "edit" : "add";
      this.conditionEditor.itemId = item ? item.id : null;
      this.conditionEditor.form = item
        ? {
            condition_type: item.condition_type || "from",
            negate: String(item.negate == 1 ? 1 : 0),
            compare_type:
              item.compare_type || this.defaultCompareTypeFor(item.condition_type),
            compare_value:
              item.compare_value ||
              this.defaultCompareValueFor(item.condition_type),
          }
        : this.buildConditionForm();

      this.modalFlow.nextModal = "segment";
      this.$bvModal.hide("domain-rule-modal");
    },

    closeConditionEditor() {
      this.modalFlow.reopenRuleModal = true;
      this.$bvModal.hide("domain-rule-segment-modal");
    },

    async submitConditionEditor() {
      if (!this.editorHasPersistedRule) {
        return;
      }

      const section = this.conditionEditor.section;
      const isEdit = this.conditionEditor.mode === "edit";
      const sectionLabel = section === "conditions" ? "Condition" : "Exception";
      const form = this.conditionEditor.form;
      const actionBase =
        section === "conditions" ? "DomainRuleCondition" : "DomainRuleException";
      const payload = {
        token: localStorage.id_token,
        domain: this.selectedDomainName,
        rid: this.editor.rid,
        condition_type: form.condition_type,
        negate: form.negate,
      };

      if (!this.validateConditionForm(form)) {
        return;
      }

      if (!this.isStructuredCondition(form.condition_type)) {
        payload.compare_type = form.compare_type;
        payload.compare_value = form.compare_value;
      }

      if (isEdit) {
        payload[
          section === "conditions" ? "condition_id" : "exception_id"
        ] = this.conditionEditor.itemId;
      }

      payload.action = (isEdit ? "edit" : "add") + actionBase;

      const res = await postRequest(payload);

      if (!res || !res.returncode) {
        this.makeToastVariant(
          "danger",
          this.responseMessage(
            res,
            "Could not save " + sectionLabel.toLowerCase() + "."
          )
        );
        return;
      }

      this.makeToastVariant(
        "success",
        this.responseMessage(res, sectionLabel + " saved.")
      );
      await this.loadRules(this.selectedDomainName, this.editor.rid);
      this.modalFlow.reopenRuleModal = true;
      this.$bvModal.hide("domain-rule-segment-modal");
    },

    async deleteCondition(section, item) {
      const sectionLabel = section === "conditions" ? "condition" : "exception";

      if (!window.confirm("Delete this " + sectionLabel + "?")) {
        return;
      }

      const res = await postRequest({
        action:
          section === "conditions"
            ? "deleteDomainRuleCondition"
            : "deleteDomainRuleException",
        token: localStorage.id_token,
        domain: this.selectedDomainName,
        rid: this.editor.rid,
        [section === "conditions" ? "condition_id" : "exception_id"]: item.id,
      });

      if (!res || !res.returncode) {
        this.makeToastVariant(
          "danger",
          this.responseMessage(res, "Could not delete " + sectionLabel + ".")
        );
        return;
      }

      this.makeToastVariant(
        "success",
        this.responseMessage(res, sectionLabel + " deleted.")
      );
      await this.loadRules(this.selectedDomainName, this.editor.rid);
    },

    async openActionEditor(item = null) {
      if (!item && !this.editorHasPersistedRule) {
        const persisted = await this.persistRule(false);
        if (!persisted) {
          return;
        }
      }

      this.actionEditor.visible = true;
      this.actionEditor.mode = item ? "edit" : "add";
      this.actionEditor.itemId = item ? item.id : null;
      this.actionEditor.form = item
        ? {
            action_type: this.normalizeActionTypeForForm(item.action_type),
            email: item.action_value || "",
            reply_type: item.reply_type || this.defaultReplyType(),
            reply_text: item.reply_text || "",
          }
        : this.buildActionForm(this.isVacationRule ? "reply" : "delete");

      this.modalFlow.nextModal = "action";
      this.$bvModal.hide("domain-rule-modal");
    },

    closeActionEditor() {
      this.modalFlow.reopenRuleModal = true;
      this.$bvModal.hide("domain-rule-action-modal");
    },

    async submitActionEditor() {
      if (!this.editorHasPersistedRule) {
        return;
      }

      const form = this.actionEditor.form;

      if (!this.validateActionForm(form)) {
        return;
      }

      const payload = {
        token: localStorage.id_token,
        domain: this.selectedDomainName,
        rid: this.editor.rid,
      };
      let action = "";
      const actionType = this.isVacationRule ? "reply" : form.action_type;

      if (this.actionEditor.mode === "edit") {
        payload.action_id = this.actionEditor.itemId;
      }

      if (actionType === "delete") {
        action =
          this.actionEditor.mode === "edit" ? "" : "addDomainRuleDeleteAction";
      } else if (actionType === "reject") {
        action =
          this.actionEditor.mode === "edit"
            ? "editDomainRuleRejectAction"
            : "addDomainRuleRejectAction";
        payload.reject_text = form.reply_text;
      } else if (actionType === "redirect" || actionType === "remote_address") {
        action =
          this.actionEditor.mode === "edit"
            ? "editDomainRuleRedirectAction"
            : "addDomainRuleRedirectAction";
        payload.email = form.email;
      } else if (actionType === "forward_copy") {
        action =
          this.actionEditor.mode === "edit"
            ? "editDomainRuleForwardCopyAction"
            : "addDomainRuleForwardCopyAction";
        payload.email = form.email;
      } else if (actionType === "reply") {
        action =
          this.actionEditor.mode === "edit"
            ? "editDomainRuleReplyAction"
            : "addDomainRuleReplyAction";
        payload.reply_type = form.reply_type;
        payload.reply_text = form.reply_text;
      } else if (actionType === "warning_message") {
        action =
          this.actionEditor.mode === "edit"
            ? "editDomainRuleWarningMessageAction"
            : "addDomainRuleWarningMessageAction";
        payload.warning_message_text = form.reply_text;
      }

      if (!action) {
        this.makeToastVariant(
          "danger",
          "Delete actions can only be added. Remove and recreate if needed."
        );
        return;
      }

      payload.action = action;

      const res = await postRequest(payload);

      if (!res || !res.returncode) {
        this.makeToastVariant(
          "danger",
          this.responseMessage(res, "Could not save action.")
        );
        return;
      }

      this.makeToastVariant(
        "success",
        this.responseMessage(res, "Action saved.")
      );
      await this.loadRules(this.selectedDomainName, this.editor.rid);
      this.modalFlow.reopenRuleModal = true;
      this.$bvModal.hide("domain-rule-action-modal");
    },

    async deleteAction(item) {
      if (!window.confirm("Delete this action?")) {
        return;
      }

      const res = await postRequest({
        action: "deleteDomainRuleAction",
        token: localStorage.id_token,
        domain: this.selectedDomainName,
        rid: this.editor.rid,
        action_id: item.id,
      });

      if (!res || !res.returncode) {
        this.makeToastVariant(
          "danger",
          this.responseMessage(res, "Could not delete action.")
        );
        return;
      }

      this.makeToastVariant(
        "success",
        this.responseMessage(res, "Action deleted.")
      );
      await this.loadRules(this.selectedDomainName, this.editor.rid);
    },

    isStructuredCondition(conditionType) {
      return ["isauthenticated", "detectforgedfrom"].includes(conditionType);
    },

    compareOptionsFor(conditionType) {
      if (conditionType === "msize") {
        return this.numericCompareOptions;
      }

      return this.textCompareOptions;
    },

    defaultCompareTypeFor(conditionType) {
      const options = this.compareOptionsFor(conditionType);
      return options.length ? options[0].value : "";
    },

    conditionValueMode(conditionType) {
      if (this.isStructuredCondition(conditionType)) {
        return "none";
      }

      if (conditionType === "msize") {
        return "number";
      }

      if (conditionType === "xpolarisspam") {
        return "select";
      }

      return "text";
    },

    conditionValueOptions(conditionType) {
      if (conditionType === "xpolarisspam") {
        return this.spamValueOptions;
      }

      return [];
    },

    defaultCompareValueFor(conditionType) {
      const options = this.conditionValueOptions(conditionType);
      return options.length ? options[0].value : "";
    },

    buildConditionForm(conditionType = "from") {
      return {
        condition_type: conditionType,
        negate: "0",
        compare_type: this.defaultCompareTypeFor(conditionType),
        compare_value: this.defaultCompareValueFor(conditionType),
      };
    },

    conditionValuePlaceholder(conditionType) {
      if (conditionType === "msize") {
        return "Enter a positive number";
      }

      if (conditionType === "attachmenttype") {
        return "Example: pdf";
      }

      return "Enter a comparison value";
    },

    onConditionTypeChange(newValue) {
      const conditionType = newValue || this.conditionEditor.form.condition_type;
      this.conditionEditor.form.compare_type =
        this.defaultCompareTypeFor(conditionType);

      if (this.conditionValueMode(conditionType) === "select") {
        this.conditionEditor.form.compare_value =
          this.defaultCompareValueFor(conditionType);
        return;
      }

      if (this.conditionValueMode(conditionType) === "none") {
        this.conditionEditor.form.compare_value = "";
        return;
      }

      this.conditionEditor.form.compare_value = "";
    },

    validateConditionForm(form) {
      if (!form.condition_type) {
        this.makeToastVariant("danger", "Condition field is required.");
        return false;
      }

      if (!this.isStructuredCondition(form.condition_type)) {
        if (!form.compare_type) {
          this.makeToastVariant("danger", "Comparison type is required.");
          return false;
        }

        if (!String(form.compare_value || "").trim()) {
          this.makeToastVariant("danger", "Condition value is required.");
          return false;
        }
      }

      return true;
    },

    actionNeedsReplyType(actionType) {
      return actionType === "reply";
    },

    actionNeedsEmail(actionType) {
      return ["redirect", "forward_copy", "remote_address"].includes(actionType);
    },

    actionNeedsText(actionType) {
      return ["reject", "reply", "warning_message"].includes(actionType);
    },

    actionTextLabel(actionType) {
      if (actionType === "reject") {
        return "Reject Message";
      }

      if (actionType === "warning_message") {
        return "Warning Message";
      }

      return "Reply Message";
    },

    actionTextPlaceholder(actionType) {
      if (actionType === "reject") {
        return "Enter the reject text returned to the sender";
      }

      if (actionType === "warning_message") {
        return "Enter the warning banner or text";
      }

      return "Enter the reply body";
    },

    validateActionForm(form) {
      const actionType = this.isVacationRule ? "reply" : form.action_type;

      if (!actionType) {
        this.makeToastVariant("danger", "Action type is required.");
        return false;
      }

      if (this.actionNeedsEmail(actionType) && !form.email) {
        this.makeToastVariant("danger", "Destination email is required.");
        return false;
      }

      if (
        this.actionNeedsText(actionType) &&
        !String(form.reply_text || "").trim()
      ) {
        this.makeToastVariant("danger", "Action text is required.");
        return false;
      }

      return true;
    },

    summarizeCondition(item) {
      const fieldLabel = this.conditionTypeLabel(item.condition_type);
      const negatePrefix = Number(item.negate) === 1 ? "NOT " : "";

      if (this.isStructuredCondition(item.condition_type)) {
        return negatePrefix + fieldLabel;
      }

      const compareLabel = this.compareLabel(item.compare_type);
      let summary =
        negatePrefix +
        fieldLabel +
        " " +
        compareLabel +
        ' "' +
        item.compare_value +
        '"';

      return summary;
    },

    summarizeAction(item) {
      if (item.action_type === "delete") {
        return "Delete message";
      }

      if (item.action_type === "redirect" || item.action_type === "remote_address") {
        return "Redirect to " + item.action_value;
      }

      if (item.action_type === "forward_copy") {
        return "Forward copy to " + item.action_value;
      }

      if (item.action_type === "reject") {
        return "Reject with: " + this.previewText(item.reply_text);
      }

      if (item.action_type === "warning_message") {
        return "Warning message: " + this.previewText(item.reply_text);
      }

      if (this.isVacationRule) {
        return (
          "Vacation reply (" +
          (item.reply_type == "1" ? "HTML" : "Plain Text") +
          "): " +
          this.previewText(item.reply_text)
        );
      }

      return (
        "Reply (" +
        (item.reply_type == "1" ? "HTML" : "Plain Text") +
        "): " +
        this.previewText(item.reply_text)
      );
    },

    defaultReplyType() {
      return this.replyTypeOptions.length ? this.replyTypeOptions[0].value : "1";
    },

    buildActionForm(actionType = "delete") {
      return {
        action_type: actionType,
        email: "",
        reply_type: this.defaultReplyType(),
        reply_text: "",
      };
    },

    normalizeActionTypeForForm(actionType) {
      if (actionType === "remote_address") {
        return "redirect";
      }

      return actionType || "delete";
    },

    onActionTypeChange(newValue) {
      const actionType = newValue || this.actionEditor.form.action_type;

      if (this.actionNeedsReplyType(actionType) && !this.actionEditor.form.reply_type) {
        this.actionEditor.form.reply_type = this.defaultReplyType();
      }

      if (!this.actionNeedsEmail(actionType)) {
        this.actionEditor.form.email = "";
      }
    },

    mapVacationSettingsToForm(settings) {
      return {
        reply_subject: settings.vacation_reply_subject || "",
        start_local: this.timestampToLocalInput(settings.vacation_start_ts),
        end_local: this.timestampToLocalInput(settings.vacation_end_ts),
      };
    },

    timestampToLocalInput(value) {
      if (!value) {
        return "";
      }

      const date = new Date(Number(value) * 1000);
      if (Number.isNaN(date.getTime())) {
        return "";
      }

      return toLocalDateTimeInput(date);
    },

    localInputToTimestamp(value) {
      const trimmed = String(value || "").trim();
      if (!trimmed) {
        return null;
      }

      const date = new Date(trimmed);
      if (Number.isNaN(date.getTime())) {
        return NaN;
      }

      return Math.floor(date.getTime() / 1000);
    },

    validateVacationSettings() {
      const startLocal = String(this.editor.vacation.start_local || "").trim();
      const endLocal = String(this.editor.vacation.end_local || "").trim();

      if ((startLocal && !endLocal) || (!startLocal && endLocal)) {
        this.makeToastVariant(
          "danger",
          "Vacation start and end must be provided together."
        );
        return false;
      }

      const startTs = this.localInputToTimestamp(startLocal);
      const endTs = this.localInputToTimestamp(endLocal);

      if (Number.isNaN(startTs) || Number.isNaN(endTs)) {
        this.makeToastVariant("danger", "Vacation date/time is invalid.");
        return false;
      }

      if (startTs !== null && endTs !== null && endTs <= startTs) {
        this.makeToastVariant(
          "danger",
          "Vacation end must be later than the start."
        );
        return false;
      }

      return true;
    },

    async saveVacationSettings(ruleId) {
      if (!this.validateVacationSettings()) {
        return false;
      }

      const payload = {
        action: "editDomainRuleVacationSettings",
        token: localStorage.id_token,
        domain: this.selectedDomainName,
        rid: ruleId,
        vacation_reply_subject:
          String(this.editor.vacation.reply_subject || "").trim() || null,
        vacation_days: "1",
      };

      const startTs = this.localInputToTimestamp(this.editor.vacation.start_local);
      const endTs = this.localInputToTimestamp(this.editor.vacation.end_local);

      payload.vacation_start_ts = startTs === null ? null : String(startTs);
      payload.vacation_end_ts = endTs === null ? null : String(endTs);

      const res = await postRequest(payload);

      if (!res || !res.returncode) {
        this.makeToastVariant(
          "danger",
          this.responseMessage(res, "Could not save vacation settings.")
        );
        return false;
      }

      return true;
    },

    conditionTypeLabel(conditionType) {
      const match = this.conditionTypeOptions.find(function(option) {
        return option.value === conditionType;
      });

      return match ? match.text : conditionType;
    },

    compareLabel(compareType) {
      const allCompareOptions = this.textCompareOptions.concat(
        this.numericCompareOptions
      );
      const match = allCompareOptions.find(function(option) {
        return option.value === compareType;
      });

      return match ? match.text : compareType;
    },

    previewText(value) {
      const plain = String(value || "")
        .replace(/<[^>]*>/g, " ")
        .replace(/\s+/g, " ")
        .trim();

      if (!plain) {
        return "No text";
      }

      if (plain.length <= 90) {
        return plain;
      }

      return plain.slice(0, 87) + "...";
    },

    responseMessage(res, fallback) {
      if (res && res.returndata) {
        return res.returndata;
      }

      return fallback;
    },

    makeToastVariant(variant = null, msg) {
      let title = this.$t("COMMON.ERROR");
      if (variant === "success") {
        title = this.$t("COMMON.SUCCESS");
      }
      this.$bvToast.toast(msg, {
        title: title,
        variant: variant,
        toaster: "b-toaster-top-center",
        solid: true,
      });
    },
  },
};
</script>

<style scoped>
.rules-page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1rem;
}

.rules-page-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #181c32;
}

.rules-page-subtitle {
  margin-top: 0.25rem;
  color: #7e8299;
  font-size: 0.925rem;
}

.rules-page-count {
  white-space: nowrap;
}

.rules-empty-state {
  border: 1px dashed #d7dae3;
  border-radius: 0.5rem;
  padding: 1.25rem;
  color: #7e8299;
  text-align: center;
  background: #fcfcfd;
}

.rules-table-wrap {
  border: 1px solid #e4e6ef;
  border-radius: 0.5rem;
  overflow: hidden;
  background: #fff;
}

.rules-table {
  width: 100%;
  border-collapse: collapse;
}

.rules-table th,
.rules-table td {
  padding: 0.85rem 1rem;
  border-bottom: 1px solid #eff2f5;
  vertical-align: middle;
}

.rules-table th {
  background: #fafbff;
  color: #7e8299;
  font-size: 0.825rem;
  font-weight: 600;
  text-align: left;
}

.rules-table tbody tr:last-child td {
  border-bottom: 0;
}

.rules-table__pos {
  width: 84px;
}

.rules-table__status {
  width: 120px;
}

.rules-table__actions {
  width: 310px;
}

.rules-table__name {
  font-weight: 600;
  color: #181c32;
}

.rules-table__action-group {
  display: flex;
  flex-wrap: wrap;
}

.rules-position {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.rules-position__value {
  min-width: 1.5rem;
  font-weight: 600;
  color: #181c32;
}

.rules-position__buttons {
  display: flex;
  gap: 0.35rem;
}

.rules-arrow-btn {
  width: 2rem;
  height: 1.75rem;
  border: 1px solid #d7dae3;
  border-radius: 0.3rem;
  background: #fff;
  color: #5e6278;
  font-size: 1rem;
  line-height: 1;
  padding: 0;
}

.rules-arrow-btn:hover {
  background: #f3f6f9;
}

.rule-modal-header {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
  margin-bottom: 1rem;
}

.rule-modal-subtitle {
  color: #7e8299;
}

.rule-modal-badges {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.rule-modal-section {
  border-top: 1px solid #eff2f5;
  padding-top: 1.25rem;
  margin-top: 1.25rem;
}

.rule-modal-section__header {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
  margin-bottom: 0.9rem;
}

.rule-modal-section__title {
  font-size: 1rem;
  font-weight: 600;
  color: #181c32;
}

.rule-modal-section__subtitle {
  margin-top: 0.25rem;
  color: #7e8299;
  font-size: 0.9rem;
}

.rule-item-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.rule-item-row {
  border: 1px solid #e4e6ef;
  border-radius: 0.5rem;
  padding: 0.9rem 1rem;
  display: flex;
  justify-content: space-between;
  gap: 1rem;
}

.rule-item-row__body {
  min-width: 0;
}

.rule-item-row__title {
  font-weight: 500;
  color: #181c32;
  word-break: break-word;
}

.rule-item-row__actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  min-width: 120px;
}

.rules-modal-footer {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}

.rules-modal-footer__actions {
  display: flex;
  align-items: center;
}

.child-modal-header {
  margin-bottom: 1rem;
}

.child-modal-back {
  padding-left: 0;
  margin-bottom: 0.35rem;
}

.child-modal-subtitle {
  color: #7e8299;
}

::v-deep .rules-modal-shell .modal-dialog,
::v-deep .rules-child-modal-shell .modal-dialog {
  max-width: 960px;
}

::v-deep .rules-modal-content,
::v-deep .rules-child-modal-content {
  border-radius: 0.65rem;
}

@media (max-width: 767.98px) {
  .rules-page-header,
  .rule-modal-header,
  .rule-modal-section__header,
  .rule-item-row,
  .rules-modal-footer {
    flex-direction: column;
    align-items: stretch;
  }

  .rules-table,
  .rules-table thead,
  .rules-table tbody,
  .rules-table tr,
  .rules-table th,
  .rules-table td {
    display: block;
  }

  .rules-table thead {
    display: none;
  }

  .rules-table tr {
    padding: 0.75rem 1rem;
    border-bottom: 1px solid #eff2f5;
  }

  .rules-table tbody tr:last-child {
    border-bottom: 0;
  }

  .rules-table td {
    padding: 0.3rem 0;
    border-bottom: 0;
  }

  .rules-table td::before {
    content: attr(data-label);
    display: block;
    color: #7e8299;
    font-size: 0.75rem;
    margin-bottom: 0.15rem;
  }

  .rules-table__pos-cell::before {
    content: "#";
  }

  .rules-table__action-group,
  .rule-item-row__actions,
  .rules-modal-footer__actions {
    justify-content: flex-start;
    min-width: 0;
  }
}

@media (max-width: 575.98px) {
  ::v-deep .rules-modal-shell .modal-dialog,
  ::v-deep .rules-child-modal-shell .modal-dialog {
    width: 100vw;
    max-width: 100vw;
    margin: 0;
    min-height: 100vh;
  }

  ::v-deep .rules-modal-content,
  ::v-deep .rules-child-modal-content {
    min-height: 100vh;
    border-radius: 0;
  }

  ::v-deep .rules-modal-shell .modal-body,
  ::v-deep .rules-child-modal-shell .modal-body {
    padding-bottom: 1.5rem;
  }
}
</style>
